import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(t("insights.all"));

  useEffect(() => {
    setActive(t("insights.all"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t("insights.all")]);

  useEffect(() => {
    (async () => {
      try {
        const cRes = await axios.get(`${API}/blog/categories`);
        setCategories([t("insights.all"), ...cRes.data.categories]);
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const isAll = active === t("insights.all");
        const url = isAll ? `${API}/blog` : `${API}/blog?category=${encodeURIComponent(active)}`;
        const res = await axios.get(url);
        setPosts(res.data);
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const others = posts.slice(0, 6);

  return (
    <section id="insights" data-testid="insights-section" className="relative py-14 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center gap-8 lg:gap-6 mb-12">
          <div>
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">{t("insights.eyebrow")}</span>
            <h2 className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] whitespace-pre-line lg:whitespace-normal">
              {t("insights.title")}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2 justify-center" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-semibold border transition-all duration-300 ${
                  active === c
                    ? "bg-[#0A192F] text-white border-[#0A192F]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#0A192F]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-12">
          {others.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`blog-card-${p.slug}`}
            >
              <Link to={`/insights/${p.slug}`} className="group block">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 mb-5">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E3A8A]">{p.category}</div>
                <h3 className="mt-3 font-heading text-lg lg:text-xl font-semibold text-[#0A192F] leading-snug group-hover:text-[#1E3A8A] transition-colors">
                  {p.title}
                </h3>
                <div className="mt-3 text-sm text-slate-500">{p.read_time}</div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
